/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import React, {
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  shallowEqual,
  useSelector,
} from 'react-redux';
import { selectLocale } from 'models/localization';
import CustomLightbox from 'components/booth/CustomLightbox';
import { LightboxContext } from 'components/booth/CustomLightbox/LightboxContext';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { TabsContext } from 'components/TabsContext';
import { selectBoothById } from 'models/cms/booths';

import BoothNavigationArrows from 'components/booth/BoothNavigationArrows';
import BoothProfileHeader from 'components/booth/BoothProfileHeader';
import { useMediaQuery } from '@material-ui/core';
import MobileProfileSwitch from './MobileProfileSwitch';
import BoothTemplateSwitch from './BoothTemplateSwitch';
import Tabs from './templates/Tabs';

export const BoothContext = React.createContext({
  id: '',
  boothNameForMatomo: '',
});

const BoothTemplateDesktopWrapper = styled.div`
  display: none;

  @media (min-width: 1200px) {
    display: block;
  }
`;

const BoothTemplateMobileWrapper = styled.div`
  display: block;

  @media (min-width: 1200px) {
    display: none;
  }
`;

const scrollDown = () => {
  document.getElementById('booth-container').scroll({ top: 1000, left: 0, behavior: 'smooth' });
};

const BoothProfileContainer = ({
  boothId, isPreview,
}) => {
  const { setActiveTabKey, tabs } = useContext(TabsContext);
  const booth = useSelector((state) => selectBoothById(state, boothId), shallowEqual);

  const lightboxContext = useContext(LightboxContext);
  const closeLightbox = useCallback(() => {
    lightboxContext.setUrl(null);
    lightboxContext.setCfStreamId(null);
  }, [lightboxContext]);

  const locale = useSelector(selectLocale);

  const boothNameForMatomo = findAvailableLocale(booth?.name || {}, 'en');

  const attachmentTabIndex = tabs.findIndex(({ masterElement, body }) => (
    body?.some((element) => masterElement?.masterControlledBody === false || (element.__typename === 'BoothFileContentElement' && element.file?.[locale]?.fileUrl))
  ));

  const isLargeDesktop = useMediaQuery('(min-width: 1200px)');

  const handleClick = (key, isCMSElement = false) => {
    switch (key) {
      case 'logo':
        if (!(booth.logoBanner?.link || booth.website)) return;
        // TODO by allen: here will had issue if is video or file, it will tracking twice
        window.trackingItemToFirebase(null, 'Booth', 'Banner', 'Banner 2_Click', 'logo', boothId, boothNameForMatomo);
        window.open(`http://${(booth.logoBanner?.link || booth.website).replace(/\b(https:\/\/|http:\/\/)/, '')}`, '_blank');
        break;
      case 'attachment':
        if (booth.hasAttachment) {
          setActiveTabKey(attachmentTabIndex);
          scrollDown();
          window.trackingItemToFirebase(null, 'Booth', 'Banner', 'Banner 4_Click', 'attachment', boothId, boothNameForMatomo);
        } else {
          if (!booth?.attachments?.length) return;
          setActiveTabKey(1);
          scrollDown();
          window.trackingItemToFirebase(null, 'Booth', 'Banner', 'Banner 4_Click', 'attachment', boothId, boothNameForMatomo);
        }
        break;
      case 'desk':
        setActiveTabKey(0);
        setTimeout(() => scrollDown(), 0);
        window.trackingItemToFirebase(null, 'Booth', 'Banner', 'Banner 3_Click', 'desk', boothId, boothNameForMatomo);
        break;
      case 'centralBanner':
        // TODO by allen: here will had issue if is video or file, it will tracking twice
        window.trackingItemToFirebase(null, 'Booth', 'Banner', 'Banner 2_Click', 'centralBanner', boothId, boothNameForMatomo);
        break;
      case 'poster':
        window.trackingItemToFirebase(null, 'Booth', 'Banner', 'Banner 1_Click', 'poster', boothId, boothNameForMatomo);
        break;
      default:
        if (isCMSElement) {
          window.trackingItemToFirebase(null, 'Booth', 'Banner', `${key}_Banner_Click`, key, boothId, boothNameForMatomo);
        }
        break;
    }
  };
  return (
    <BoothContext.Provider value={{ ...booth, boothNameForMatomo }}>
      <BoothProfileHeader boothId={boothId} />
      {
        isLargeDesktop ? (
          <BoothTemplateDesktopWrapper className="booth-template-wrapper">
            <BoothTemplateSwitch
              booth={booth}
              handleClick={handleClick}
              showRepsImg
              showPlayBtn
            />
            <BoothNavigationArrows isPreview={isPreview} />
          </BoothTemplateDesktopWrapper>
        ) : (
          <BoothTemplateMobileWrapper className="booth-template-wrapper">
            <MobileProfileSwitch booth={booth} />
          </BoothTemplateMobileWrapper>
        )
      }
      <Tabs
        booth={booth}
      />
      <CustomLightbox
        src={lightboxContext.url}
        cfStreamId={lightboxContext.cfStreamId}
        open={!!(lightboxContext.cfStreamId || lightboxContext.url)}
        onClose={closeLightbox}
        type="video"
      />
    </BoothContext.Provider>
  );
};

BoothProfileContainer.propTypes = {
  boothId: PropTypes.string.isRequired,
  isPreview: PropTypes.bool,
};

BoothProfileContainer.defaultProps = {
  isPreview: false,
};

export default React.memo(BoothProfileContainer);
